import React, { useState } from "react";
import { Loader2, ShieldCheck, Mail } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useLang } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { GRADES } from "./Signup";

export default function Profile() {
  const { t } = useLang();
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "", mobile: user?.mobile || "", grade: user?.grade || "", school: user?.school || "",
  });
  const [pw, setPw] = useState({ currentPassword: "", newPassword: "" });
  const [busy, setBusy] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const isGoogle = user?.authProvider === "google";
  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/api/auth/profile", form);
      await refresh();
      toast.success(t("profile.saved"));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const changePw = async (e) => {
    e.preventDefault();
    setPwBusy(true);
    try {
      await api.post("/api/auth/change-password", pw);
      toast.success(t("profile.pw.done"));
      setPw({ currentPassword: "", newPassword: "" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-14" data-testid="profile-page">
      <h1 className="font-display text-4xl font-semibold sm:text-5xl" data-testid="profile-title">{t("profile.title")}</h1>
      <p className="mt-3 text-base text-muted-foreground md:text-lg">{t("profile.sub")}</p>

      <form onSubmit={save} className="mt-8 space-y-4 rounded-2xl border border-border bg-card p-6" data-testid="profile-form">
        <h2 className="font-display text-lg font-semibold">{t("profile.info")}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="p-name">{t("auth.name")}</Label>
            <Input id="p-name" required value={form.name} onChange={set("name")} data-testid="profile-name-input" />
          </div>
          <div>
            <Label htmlFor="p-email">{t("auth.email")}</Label>
            <Input id="p-email" value={user?.email || ""} disabled data-testid="profile-email" />
          </div>
          <div>
            <Label htmlFor="p-mobile">{t("auth.mobile")}</Label>
            <Input id="p-mobile" value={form.mobile} onChange={set("mobile")} data-testid="profile-mobile-input" />
          </div>
          <div>
            <Label>{t("auth.grade")}</Label>
            <Select value={form.grade} onValueChange={(v) => setForm({ ...form, grade: v })}>
              <SelectTrigger data-testid="profile-grade-select"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{GRADES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="p-school">{t("auth.school")}</Label>
            <Input id="p-school" value={form.school} onChange={set("school")} data-testid="profile-school-input" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4 pt-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{t("profile.provider")}: {isGoogle ? "Google" : "Email"}</span>
          <span className="flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" />{t("profile.member")} {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : "—"}</span>
        </div>
        <Button type="submit" disabled={busy} data-testid="profile-save-button">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t("admin.save")}
        </Button>
      </form>

      {!isGoogle && (
        <form onSubmit={changePw} className="mt-6 space-y-4 rounded-2xl border border-border bg-card p-6" data-testid="password-form">
          <h2 className="font-display text-lg font-semibold">{t("profile.pw.title")}</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="p-cur">{t("profile.pw.current")}</Label>
              <Input id="p-cur" type="password" required value={pw.currentPassword}
                     onChange={(e) => setPw({ ...pw, currentPassword: e.target.value })} data-testid="pw-current-input" />
            </div>
            <div>
              <Label htmlFor="p-new">{t("profile.pw.new")}</Label>
              <Input id="p-new" type="password" required minLength={6} value={pw.newPassword}
                     onChange={(e) => setPw({ ...pw, newPassword: e.target.value })} data-testid="pw-new-input" />
            </div>
          </div>
          <Button type="submit" variant="outline" disabled={pwBusy} data-testid="pw-change-button">
            {pwBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t("profile.pw.btn")}
          </Button>
        </form>
      )}
    </div>
  );
}
